import React from "react";
import { MdEdit, MdDelete, MdFlag } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Card from "components/card";
import { ReportedPost, Post } from "../../types/community";
import { Author } from "../../types/community";
import { useAuth } from "../../contexts/AuthContext";
import EmptyState from "../common/EmptyState";
import LoadingSpinner from "../common/LoadingSpinner";

interface ReportedPostsTableProps {
  reportedPosts: ReportedPost[];
  loading: boolean;
  onEdit: (post: Post) => void;
  onDelete: (post: Post) => void;
}

const ReportedPostsTable: React.FC<ReportedPostsTableProps> = ({
  reportedPosts,
  loading,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    const formatter = new Intl.DateTimeFormat("en-US", options);
    const formatted = formatter.format(date);

    // Add ordinal suffix to day
    const day = date.getDate();
    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
        ? "rd"
        : "th";

    return formatted.replace(/\d+/, `${day}${suffix}`);
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return "—";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ""}${
      lastName?.charAt(0) || ""
    }`.toUpperCase();
  };

  const canEditPost = (post: Post) => {
    return user && (user.id === post.author.id || user.role === "admin");
  };

  const canDeletePost = (post: Post) => {
    return user && (user.id === post.author.id || user.role === "admin");
  };

  const handleViewPost = (post: Post) => {
    navigate(`/admin/community/${post.id}`);
  };

  const handleEditClick = (e: React.MouseEvent, post: Post) => {
    e.stopPropagation();
    onEdit(post);
  };

  const handleDeleteClick = (e: React.MouseEvent, post: Post) => {
    e.stopPropagation();
    onDelete(post);
  };

  const renderUserInfo = (author: Author) => {
    const authorFullName = `${author.first_name || ""} ${
      author.last_name || ""
    }`.trim();
    const authorDisplayName = authorFullName || author.email;
    const isDeletedUser = !!author.deleted_at;

    return (
      <div className="flex items-center">
        <div className="h-10 w-10 flex-shrink-0">
          {author.profile_image?.full_path ? (
            <img
              src={author.profile_image.full_path}
              alt={authorDisplayName}
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-biafit-primary">
              <span className="text-sm font-medium text-white">
                {getInitials(author.first_name, author.last_name)}
              </span>
            </div>
          )}
        </div>
        <div className="ml-4">
          {isDeletedUser && (
            <div className="mb-0.5 text-xs font-medium text-red-600 dark:text-red-400">
              Deleted User
            </div>
          )}
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {authorDisplayName}
          </div>
          {author.email && author.email !== authorDisplayName && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {author.email}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card extra="w-full pb-10 p-4 h-full">
        <div className="mt-8 flex items-center justify-center">
          <LoadingSpinner bare size="lg" />
        </div>
      </Card>
    );
  }

  if (reportedPosts.length === 0) {
    return (
      <Card extra="w-full pb-10 p-4 h-full">
        <EmptyState icon={MdFlag} title="No reported posts found" />
      </Card>
    );
  }

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden md:block">
        <Card extra="w-full pb-10 p-3 md:p-6 h-full">
          <header className="relative flex items-center justify-between">
            <div className="text-xl font-bold text-navy-700 dark:text-white">
              Reported Posts
            </div>
          </header>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-navy-800">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  >
                    Report Reason
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  >
                    Post Content
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  >
                    Post Author
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  >
                    Reporter
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  >
                    Report Date
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-navy-900">
                {reportedPosts.map((report) => (
                  <tr
                    key={report.id}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-800"
                    onClick={() => handleViewPost(report.post)}
                  >
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="min-w-[400px] max-w-[500px] whitespace-break-spaces text-sm font-semibold text-biafit-primary">
                        {report.reason}
                      </div>
                    </td>
                    <td className="min-w-[200px] px-6 py-4">
                      <div className="max-w-md text-sm text-gray-900 dark:text-white">
                        {truncateText(report.post.content)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      {renderUserInfo(report.post.author)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      {renderUserInfo(report.reporter)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {formatDate(report.created_at)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {canEditPost(report.post) && (
                          <button
                            onClick={(e) => handleEditClick(e, report.post)}
                            className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                            title="Edit Post"
                          >
                            <MdEdit className="h-5 w-5" />
                          </button>
                        )}
                        {canDeletePost(report.post) && (
                          <button
                            onClick={(e) => handleDeleteClick(e, report.post)}
                            className="text-gray-600 transition-colors hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                            title="Delete Post"
                          >
                            <MdDelete className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Mobile Card View */}
      <div className="grid grid-cols-1 gap-4 md:hidden">
        {reportedPosts.map((report) => (
          <Card
            key={report.id}
            extra="p-4 cursor-pointer"
            onClick={() => handleViewPost(report.post)}
          >
            <div className="space-y-4">
              {/* Header with Actions */}
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Report Reason
                  </div>
                  <div className="mt-2 text-sm font-semibold text-biafit-primary">
                    {report.reason}
                  </div>
                </div>
                <div className="flex flex-col items-center gap-2 md:flex-row">
                  {canEditPost(report.post) && (
                    <button
                      onClick={(e) => handleEditClick(e, report.post)}
                      className="p-1 text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                      <MdEdit className="h-5 w-5" />
                    </button>
                  )}
                  {canDeletePost(report.post) && (
                    <button
                      onClick={(e) => handleDeleteClick(e, report.post)}
                      className="p-1 text-gray-600 transition-colors hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                    >
                      <MdDelete className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Report Reason Value */}

              {/* Post Content */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Post Content
                </div>
                <div className="mt-1 text-sm text-gray-900 dark:text-white">
                  {truncateText(report.post.content)}
                </div>
              </div>

              {/* Post Author */}
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Post Author
                </div>
                {renderUserInfo(report.post.author)}
              </div>

              {/* Reporter */}
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Reporter
                </div>
                {renderUserInfo(report.reporter)}
              </div>

              {/* Report Date */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Report Date
                </div>
                <div className="mt-1 text-sm text-gray-900 dark:text-white">
                  {formatDate(report.created_at)}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
};

export default ReportedPostsTable;
